/**
 * 登录后该落到哪一端
 * ---------------------------------------------------------------------------
 * 老板端和员工端是两套信息架构（产品文档 §5.6），不是同一套界面藏几个菜单。
 * 所以入口判断只做一次，集中在这里，路由表和「我的」页都用同一个结论，
 * 不要在各自的组件里再写一份 can() 组合——两处判断迟早会不一致。
 *
 * 默认入口由服务端角色配置决定。权限仍用于控制页面和动作，但不能再拿权限
 * 反推角色：发型师将来即使获准查看会员，也不应该因此被送进老板端。
 */

/**
 * 老板端的入口权限。有其中任意一个就说明这个账号在老板端至少有一页能用。
 * 这些权限码来自 server/prisma/seed.ts 的权限表，改动要和服务端对齐。
 */
const CONSOLE_ENTRY_PERMISSIONS = [
  'order.create',
  'order.read',
  'member.read',
  'report.read',
  'staff.read',
  'settings.read',
];

/** 员工端全部四页共用的权限，和 /employee/* 接口要求的一致 */
export const EMPLOYEE_PERMISSION = 'staff.self.read';

export function canUseConsole(can: (permission: string) => boolean): boolean {
  return CONSOLE_ENTRY_PERMISSIONS.some((code) => can(code));
}

export function canUseEmployeeApp(
  can: (permission: string) => boolean,
): boolean {
  return can(EMPLOYEE_PERMISSION);
}

/**
 * 打开应用后的落地地址。
 *
 * 优先用服务端角色上配置的 `defaultEntry`——入口属于角色配置，
 * 不该由权限组合反推（发型师将来即使获准查看会员，也不应该被送进老板端）。
 *
 * **但拿不到这个字段时不能一律去老板端。** 部署不同步是常态：
 * 前端先发、后端容器还是旧镜像，或者迁移应用了但服务没重启，`defaultEntry`
 * 就会是 undefined。这时把只有 `staff.self.read` 的发型师送进老板端，
 * 他看到的是一个首页就报「没有权限」的死胡同——正是这个字段要消除的问题。
 * 2026-08-02 就实际复现过一次（API 镜像未重建）。
 *
 * 所以字段缺失时回退到能力判断：老板端一页都用不上就去员工端。
 * 这条回退是**兜底**，不是并列的判断依据——字段存在时永远以字段为准。
 */
export function resolveHomePath(
  defaultEntry: 'console' | 'employee' | undefined,
  can: (permission: string) => boolean,
): string {
  if (defaultEntry === 'employee') return '/m/today';
  if (defaultEntry === 'console') return '/dashboard';

  // 字段缺失：按"哪一端真的用得起来"兜底
  if (!canUseConsole(can) && canUseEmployeeApp(can)) return '/m/today';
  return '/dashboard';
}
